import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { hmdaApi, fairLendingApi } from '../../lib/api';
import { useTeamId } from '../../hooks/useTeamId';
import toast from 'react-hot-toast';

const ANALYSIS_TYPES = [
  { value: 'DENIAL_RATE', label: 'Denial Rate Analysis', description: 'Compare denial rates by race, ethnicity, and sex' },
  { value: 'PRICING', label: 'Pricing Analysis', description: 'Compare rate spreads by demographic group' },
  { value: 'GEOGRAPHIC', label: 'Geographic Analysis', description: 'Analyze lending patterns across census tracts' },
];

export default function AnalysisModal({
  isOpen,
  onClose,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const { teamId } = useTeamId();
  const [selectedValidation, setSelectedValidation] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<string[]>(['DENIAL_RATE', 'PRICING', 'GEOGRAPHIC']);

  // Fetch user's HMDA validations
  const { data: validationsData } = useQuery({
    queryKey: ['hmda-validations-for-fl'],
    queryFn: async () => {
      const res = await hmdaApi.getValidations({ limit: 100 });
      return res.data;
    },
    enabled: isOpen,
  });

  const validations = validationsData?.validations || [];

  const runMutation = useMutation({
    mutationFn: () =>
      fairLendingApi.runAnalysis(teamId!, {
        validationId: selectedValidation,
        analysisTypes: selectedTypes,
      }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['fair-lending'] });
      toast.success('Analysis completed');
      onClose();
      if (onSuccess && res.data?.analysis?.id) {
        onSuccess(res.data.analysis.id);
      }
    },
    onError: () => toast.error('Analysis failed. Please try again.'),
  });

  const toggleType = (type: string) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const canSubmit = selectedValidation && selectedTypes.length > 0 && !runMutation.isPending;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg bg-white rounded-xl shadow-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    Run Fair Lending Analysis
                  </Dialog.Title>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-5">
                  {/* Validation selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      HMDA Validation Dataset
                    </label>
                    <select
                      value={selectedValidation}
                      onChange={(e) => setSelectedValidation(e.target.value)}
                      className="block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-ocean-500 focus:ring-ocean-500"
                    >
                      <option value="">Select a validation...</option>
                      {validations.map((v: any) => (
                        <option key={v.id} value={v.id}>
                          {v.fileName} — {v.totalLoans} loans ({v.institutionName || 'Unknown'})
                        </option>
                      ))}
                    </select>
                    {validations.length === 0 && (
                      <p className="mt-1 text-xs text-gray-400">
                        No HMDA validations found. Upload and validate a LAR file first.
                      </p>
                    )}
                  </div>

                  {/* Analysis type checkboxes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Analysis Types
                    </label>
                    <div className="space-y-2">
                      {ANALYSIS_TYPES.map((type) => (
                        <label key={type.value} className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedTypes.includes(type.value)}
                            onChange={() => toggleType(type.value)}
                            className="mt-0.5 rounded border-gray-300 text-ocean-600 focus:ring-ocean-500"
                          />
                          <div>
                            <span className="text-sm font-medium text-gray-900">{type.label}</span>
                            <p className="text-xs text-gray-500">{type.description}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                </div>

                {/* Actions */}
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    onClick={onClose}
                    className="rounded-md px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => runMutation.mutate()}
                    disabled={!canSubmit}
                    className="rounded-md px-4 py-2 text-sm font-medium bg-ocean-600 text-white hover:bg-ocean-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {runMutation.isPending ? 'Running Analysis...' : 'Run Analysis'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
